import { z } from 'zod'

// Auth schemas
export const registerSchema = z.object({
  orgName: z.string().min(2, 'Organisation name must be at least 2 characters').max(100),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters').max(100),
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
})

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

export type RegisterInput = z.infer<typeof registerSchema>
export type LoginInput = z.infer<typeof loginSchema>

// Room schemas
export const createRoomSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  descriptionHtml: z.string().optional(),
  capacity: z.number().int().positive().default(1),
  hourlyRate: z.number().nonnegative().default(0),
  amenities: z.array(z.string()).default([]),
  location: z.string().optional(),
})

export const updateRoomSchema = createRoomSchema.partial()

export type CreateRoomInput = z.infer<typeof createRoomSchema>
export type UpdateRoomInput = z.infer<typeof updateRoomSchema>

// Room schedule schema
export const roomScheduleSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  openTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format'),
  closeTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format'),
  isActive: z.boolean().default(true),
})

export type RoomScheduleInput = z.infer<typeof roomScheduleSchema>

// Booking schemas
export const createBookingSchema = z.object({
  roomId: z.string().min(1, 'Room is required'),
  title: z.string().max(200).optional(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
  notes: z.string().max(1000).optional(),
}).refine((data) => data.endTime > data.startTime, {
  message: 'End time must be after start time',
  path: ['endTime'],
})

export const updateBookingSchema = z.object({
  title: z.string().max(200).optional(),
  startTime: z.coerce.date().optional(),
  endTime: z.coerce.date().optional(),
  notes: z.string().max(1000).optional(),
})

export type CreateBookingInput = z.infer<typeof createBookingSchema>
export type UpdateBookingInput = z.infer<typeof updateBookingSchema>

// User management schemas
export const inviteUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(2).max(100).optional(),
  role: z.enum(['admin', 'approver', 'guest']).default('guest'),
  attributes: z.object({
    requiresApproval: z.boolean().optional(),
    discountCode: z.string().nullable().optional(),
    canBookDirectly: z.boolean().optional(),
  }).optional(),
})

export const updateUserSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  role: z.enum(['admin', 'approver', 'guest']).optional(),
  attributes: z.object({
    requiresApproval: z.boolean().optional(),
    discountCode: z.string().nullable().optional(),
    canBookDirectly: z.boolean().optional(),
  }).optional(),
  status: z.enum(['active', 'suspended']).optional(),
})

export type InviteUserInput = z.infer<typeof inviteUserSchema>
export type UpdateUserInput = z.infer<typeof updateUserSchema>

// Approval schemas
export const approvalDecisionSchema = z.object({
  status: z.enum(['approved', 'rejected']),
  note: z.string().max(500).optional(),
})

export type ApprovalDecisionInput = z.infer<typeof approvalDecisionSchema>
